import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { MessageCircle } from 'lucide-react';
import { getAuthHeaders, getUser, API_URL } from '../utils/auth';

const API = API_URL;

/**
 * WhatsApp Floating Chat Button
 *
 * Displays a floating WhatsApp chat button based on tenant settings.
 * Only visible when:
 * - User is authenticated
 * - Tenant has whatsapp_enabled = true
 * - Tenant has a valid whatsapp_number configured
 *
 * Configuration (from tenant settings):
 * - whatsapp_enabled: boolean
 * - whatsapp_number: string (phone number with country code)
 * - whatsapp_message: string (optional pre-filled message)
 * - whatsapp_position: 'bottom-right' | 'bottom-left'
 * - whatsapp_label: string (optional button label)
 */
const WhatsAppChatButton = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    // Only fetch config if user is authenticated
    const user = getUser();
    if (user) {
      fetchWhatsAppConfig();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchWhatsAppConfig = async () => {
    try {
      // v2.37.1: Config-driven visibility (no hostname checks)
      // v1.4.41: Use public endpoint that doesn't require admin role
      const response = await axios.get(`${API}/tenant/public-settings`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });

      const settings = response.data?.settings || {};

      const type = settings.whatsapp_type || 'direct';
      const hasTarget = type === 'group'
        ? !!settings.whatsapp_group_link
        : !!settings.whatsapp_number;

      // Hide button if tenant has no WhatsApp configured
      if (!hasTarget) {
        setLoading(false);
        return;
      }

      if (settings.whatsapp_enabled && hasTarget) {
        setConfig({
          enabled: settings.whatsapp_enabled,
          type,
          number: (settings.whatsapp_number || '').replace(/[^0-9]/g, ''),
          group_link: settings.whatsapp_group_link || '',
          message: settings.whatsapp_message || '',
          position: settings.whatsapp_position || 'bottom-right',
          label: settings.whatsapp_label || ''
        });
      }
    } catch (error) {
      // Silently fail - WhatsApp button is optional
    } finally {
      setLoading(false);
    }
  };

  // Don't render if loading, disabled, or no config
  if (loading || !config || !config.enabled) {
    return null;
  }

  const whatsappUrl = config.type === 'group'
    ? config.group_link
    : `https://wa.me/${config.number}${config.message ? `?text=${encodeURIComponent(config.message)}` : ''}`;

  const positionClasses = config.position === 'bottom-left'
    ? 'left-4 sm:left-6'
    : 'right-4 sm:right-6';

  const handleClick = () => {
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      {/* Floating Button Container */}
      {/* Mobile: bottom-20 to clear BottomNavigation (h-16 + safe area) */}
      {/* Desktop: bottom-6 as before */}
      <div
        className={`fixed bottom-20 md:bottom-6 ${positionClasses} z-50 flex flex-col items-end gap-2`}
      >
        {/* Tooltip / Expanded Label */}
        {(showTooltip || isExpanded) && config.label && (
          <div
            className="bg-white px-3 py-2 rounded-lg shadow-lg border border-gray-200 text-sm text-gray-700 animate-fade-in"
            style={{ animation: 'fadeIn 0.2s ease-out' }}
          >
            {config.label}
          </div>
        )}

        {/* Main Button */}
        {/* Mobile: larger touch target (p-5) for easier tapping */}
        <button
          onClick={handleClick}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
          className={`
            flex items-center justify-center gap-2
            bg-[#25D366] hover:bg-[#20BD5A]
            text-white
            rounded-full
            shadow-lg hover:shadow-xl
            transition-all duration-300 ease-out
            transform hover:scale-105 active:scale-95
            min-w-[56px] min-h-[56px]
            ${isExpanded && config.label ? 'px-5 py-3' : 'p-4 md:p-4'}
          `}
          aria-label="Chat on WhatsApp"
        >
          <MessageCircle className="h-6 w-6 md:h-6 md:w-6" />
          {isExpanded && config.label && (
            <span className="font-medium whitespace-nowrap">{config.label}</span>
          )}
        </button>

        {/* Pulse Animation Ring */}
        <div
          className={`
            absolute bottom-0 ${config.position === 'bottom-left' ? 'left-0' : 'right-0'}
            w-14 h-14 min-w-[56px] min-h-[56px]
            bg-[#25D366]
            rounded-full
            animate-ping
            opacity-20
            pointer-events-none
          `}
          style={{ animationDuration: '2s' }}
        />
      </div>

      {/* Custom Animation Styles */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </>
  );
};

export default WhatsAppChatButton;
